import {
  type DataTableProps,
  type SetFilterModel,
  type NumberFilterModel,
  type TextFilterModel,
  type IMultiFilterModel,
} from "@tokenterminal/ui/DataTable"
import { getPreset } from "api/ApiCalls"
import { useTracking } from "components/Tracking"
import { useAuth } from "context/AuthContext"
import { sendEvent } from "modules/analytics"
import {
  ACCESS_ACTIONS,
  ACCESS_COMPONENTS,
  useAccess,
} from "modules/auth/useAccess"
import { LazyAuthPaywallModal } from "modules/paywall/LazyAuthPaywallModal"
import Router, { useRouter } from "next/router"
import { startTransition, useEffect, useState } from "react"
import { DataGrid } from "./DataGrid"
import { useDataGridState } from "./DataGridContext"
import { type MartPreset } from "./types"

export * from "@tokenterminal/ui/DataTable"

interface InternalProps {
  withPaywall?: boolean
  suppressDownloads?: boolean
  initialFilter?: Record<
    string,
    SetFilterModel | NumberFilterModel | TextFilterModel | IMultiFilterModel
  >
}

export function DataGridWithPresetQuery<
  T extends Record<string, unknown>,
  C = {},
>({ data, ...props }: DataTableProps<T, C> & InternalProps) {
  const [_, setGridState] = useDataGridState<T>()
  const [preset, setPreset] = useState<null | MartPreset>(null)
  const { contextValue } = useTracking()
  const auth = useAuth()
  const router = useRouter()
  const [isMountedPresetReady, setMountedPresetReady] = useState(false)
  const canViewPreset = useAccess(
    ACCESS_COMPONENTS.DATASETS,
    ACCESS_ACTIONS.PRESET_SAVE
  )

  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false)

  // Applying and resetting state is quite expensive, so we want to update the ui first and then apply the state by using two react renders on mount.
  useEffect(() => {
    if (router.isReady && !isMountedPresetReady && router.query.preset) {
      /*
       * NOTE: If wanted to do better, we would need a ref in addition to this to tell to skip this if initial mount doesnt have preset (query param).
       * Issue comes from initial state not being populated based on query param because it's not available on this route at server side. This affects
       * the first selected preset to be applied slower and with 2 renders
       */
      setMountedPresetReady(true)
    }
  }, [router.isReady, isMountedPresetReady, router.query.preset])

  // TODO: Add support for datasource
  useEffect(() => {
    const presetId = router.query.preset

    // Wait for authentication and router to be initialized
    if (
      !router.isReady ||
      presetId === null ||
      Array.isArray(presetId) ||
      !auth.isLoginFinished
    ) {
      return
    }

    if (!presetId) {
      setPreset(null)
      ;(async () => {
        // Reset possible preset in context
        startTransition(() => {
          setGridState((state) => ({
            ...state,
            preset: null,
          }))
        })
      })()
      return
    }

    if (presetId && !canViewPreset) {
      setIsAuthModalOpen(true)

      sendEvent("wall", {
        action: "preset_view",
        wall_type: "auth",
        component: "datatable",
        name: presetId,
        filters: {},
        page_id: contextValue.pageId,
        page_type: contextValue.pageType!,
      })

      const url = new URL(window.location.href)
      url.searchParams.delete("preset")
      Router.replace(url, undefined, { shallow: true })

      return
    }

    // Opt out if preset is being applied too fast.
    if (!isMountedPresetReady) {
      return
    }

    ;(async () => {
      try {
        const preset = await getPreset(presetId)

        if (!preset) {
          const url = new URL(window.location.href)
          url.searchParams.delete("preset")
          Router.replace(url, undefined, { shallow: true })

          return
        }

        setPreset(preset)

        setGridState((state) => ({
          ...state,
          martId: preset.mart_id,
          preset: {
            name: preset.name,
            id: preset.id,
            type: preset.type,
          },
        }))
      } catch (error) {
        console.error("failed to set preset", error)
      }
    })()
  }, [
    router.query.preset,
    router.isReady,
    auth.isLoginFinished,
    auth.user,
    isMountedPresetReady,
    setGridState,
    canViewPreset,
    contextValue.pageId,
    contextValue.pageType,
  ])

  return (
    <>
      <DataGrid {...props} data={data} preset={preset} />
      <LazyAuthPaywallModal
        onClose={() => setIsAuthModalOpen(false)}
        isOpen={isAuthModalOpen}
        setIsOpen={setIsAuthModalOpen}
      />
    </>
  )
}
